// =============================================================================
// Notification bell — badge with unread count, click for a dropdown of the
// 8 most recent unread items + "See all" link to the full /notifications
// page. Self-contained; safe to drop into any header.
// =============================================================================

import { useState, useRef, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Bell, Check, X as XIcon, ExternalLink, AlertTriangle, AlertCircle, Info } from 'lucide-react'
import clsx from 'clsx'
import { useNotifications, type MyNotification, type NotificationSeverity } from '@/hooks/useNotifications'
import { fmtDateZA } from '@/lib/dates'

const SEVERITY_ICON: Record<NotificationSeverity, typeof AlertTriangle> = {
  CRITICAL: AlertCircle,
  WARNING:  AlertTriangle,
  INFO:     Info,
}

const SEVERITY_TONE: Record<NotificationSeverity, string> = {
  CRITICAL: 'text-red-400',
  WARNING:  'text-amber-400',
  INFO:     'text-blue-400',
}

export default function NotificationBell({ className }: { className?: string }) {
  const { list, unread, markRead, dismiss, markAllRead } = useNotifications()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  // Click-outside to close
  useEffect(() => {
    if (!open) return
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const recent = (list.data ?? []).filter(n => !n.dismissed_at).slice(0, 8)

  function go(n: MyNotification) {
    markRead.mutate(n.notification_id)
    setOpen(false)
    if (n.link_path) navigate(n.link_path)
  }

  return (
    <div ref={ref} className={clsx('relative', className)}>
      <button onClick={() => setOpen(o => !o)}
        className="relative p-2 coarse:min-w-[44px] coarse:min-h-[44px] -m-[5px] sm:m-0 rounded-xl hover:bg-surface text-fg-secondary"
        aria-label="Notifications">
        <Bell size={18} />
        {unread.length > 0 && (
          <span className="absolute top-[3px] right-[3px] sm:-top-0.5 sm:-right-0.5 min-w-[16px] h-4 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center px-1">
            {unread.length > 99 ? '99+' : unread.length}
          </span>
        )}
      </button>

      {open && (
        <div data-theme="dark" className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] z-50 surface-sunk border hairline-strong rounded-2xl shadow-xl overflow-hidden">
          <header className="px-3 py-2 border-b hairline flex items-center justify-between">
            <p className="text-xs font-semibold text-fg-secondary">
              Notifications
              {unread.length > 0 && <span className="ml-1.5 text-fg-muted">({unread.length} unread)</span>}
            </p>
            {unread.length > 0 && (
              <button onClick={() => markAllRead.mutate()}
                className="tap-target-y text-[11px] text-blue-400 hover:text-blue-300 px-2 py-1.5 -mx-2 -my-1.5 rounded-lg">
                Mark all read
              </button>
            )}
          </header>

          <ul className="max-h-[60dvh] overflow-y-auto divide-y divide-line-subtle">
            {recent.length === 0 ? (
              <li className="p-6 text-center text-xs text-fg-muted">
                No notifications
              </li>
            ) : (
              recent.map(n => <BellRow key={n.notification_id} n={n} onGo={go}
                onMarkRead={() => markRead.mutate(n.notification_id)}
                onDismiss={() => dismiss.mutate(n.notification_id)}
              />)
            )}
          </ul>

          <footer className="px-3 py-2 border-t hairline text-center">
            <Link to="/notifications" onClick={() => setOpen(false)}
              className="text-xs text-blue-400 hover:text-blue-300">
              See all
            </Link>
          </footer>
        </div>
      )}
    </div>
  )
}

function BellRow({ n, onGo, onMarkRead, onDismiss }: {
  n: MyNotification
  onGo: (n: MyNotification) => void
  onMarkRead: () => void
  onDismiss: () => void
}) {
  const Icon = SEVERITY_ICON[n.severity]
  const isUnread = !n.read_at
  return (
    <li className={clsx('px-3 py-2 hover:bg-surface/50', isUnread && 'bg-surface/30')}>
      <div className="flex items-start gap-2">
        <Icon size={14} className={clsx('mt-0.5 shrink-0', SEVERITY_TONE[n.severity])} />
        <div className="flex-1 min-w-0">
          <button onClick={() => onGo(n)} className="text-left w-full">
            <p className={clsx('text-xs leading-tight truncate', isUnread ? 'font-semibold text-white' : 'text-fg-secondary')}>
              {n.title}
            </p>
            {n.body && <p className="text-[11px] text-fg-muted truncate mt-0.5">{n.body}</p>}
            <p className="text-[10px] text-fg-muted mt-0.5">
              {timeAgo(n.created_at)}
            </p>
          </button>
        </div>
        <div className="flex flex-col gap-1.5 sm:gap-0.5 shrink-0">
          {n.link_path && (
            <button onClick={() => onGo(n)} title="Open" aria-label={`Open ${n.title}`}
              className="tap-target p-2 sm:p-1 rounded hover:bg-raised text-fg-muted hover:text-white">
              <ExternalLink size={11} className="size-3.5 sm:size-[11px]" />
            </button>
          )}
          {isUnread && (
            <button onClick={onMarkRead} title="Mark read" aria-label={`Mark "${n.title}" read`}
              className="tap-target p-2 sm:p-1 rounded hover:bg-raised text-fg-muted hover:text-emerald-300">
              <Check size={11} className="size-3.5 sm:size-[11px]" />
            </button>
          )}
          <button onClick={onDismiss} title="Dismiss" aria-label={`Dismiss "${n.title}"`}
            className="tap-target p-2 sm:p-1 rounded hover:bg-raised text-fg-muted hover:text-red-400">
            <XIcon size={11} className="size-3.5 sm:size-[11px]" />
          </button>
        </div>
      </div>
    </li>
  )
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  const m = Math.floor(ms / 60000)
  if (m < 1)   return 'just now'
  if (m < 60)  return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24)  return `${h}h ago`
  const d = Math.floor(h / 24)
  if (d < 30)  return `${d}d ago`
  return fmtDateZA(iso)
}
